import React, { useState } from "react";

interface NearMiss {
  id: string;
  date: string;
  area: string;
  description: string;
  immediateAction: string;
  rootCause: string;
  learning: string;
  sharedInMeeting: boolean;
  sharedDate: string;
  reportedBy: string;
}

const KEY = "near_miss_records";
const AREAS = ["Medication", "Falls", "Equipment", "Kitchen / Nutrition", "Moving & Handling", "Documentation", "Other"];

function load(): NearMiss[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

export default function NearMiss({ user }: { user?: any }) {
  const [items, setItems] = useState<NearMiss[]>(load);
  const [form, setForm] = useState<Partial<NearMiss>>({ date: new Date().toISOString().slice(0, 10), area: "Medication", reportedBy: user && user.name ? user.name : "" });
  const [message, setMessage] = useState("");

  const save = (next: NearMiss[]) => {
    setItems(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const add = () => {
    if (!form.description) {
      setMessage("Describe what nearly happened.");
      return;
    }
    save([{
      id: `nm-${Date.now()}`,
      date: form.date || new Date().toISOString().slice(0, 10),
      area: form.area || "Other",
      description: form.description || "",
      immediateAction: form.immediateAction || "",
      rootCause: form.rootCause || "",
      learning: form.learning || "",
      sharedInMeeting: false,
      sharedDate: "",
      reportedBy: form.reportedBy || (user && user.name ? user.name : ""),
    }, ...items]);
    setForm({ date: new Date().toISOString().slice(0, 10), area: "Medication", reportedBy: user && user.name ? user.name : "" });
    setMessage("Near miss recorded - thank you for reporting. No blame, just learning.");
  };

  const markShared = (id: string) => {
    save(items.map(i => (i.id === id ? { ...i, sharedInMeeting: true, sharedDate: new Date().toISOString().slice(0, 10) } : i)));
  };

  const remove = (id: string) => save(items.filter(i => i.id !== id));

  const byArea = AREAS.map(area => ({ area, count: items.filter(i => i.area === area).length })).sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...byArea.map(b => b.count));
  const thisMonth = items.filter(i => (i.date || "").slice(0, 7) === new Date().toISOString().slice(0, 7)).length;
  const shared = items.filter(i => i.sharedInMeeting).length;

  const exportPrint = () => {
    const rows = items.map(i => `<tr><td style="border-bottom:1px solid #eee;padding:6px">${i.date}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.area}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.description}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.rootCause || "-"}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.learning || "-"}</td><td style="border-bottom:1px solid #eee;padding:6px">${i.sharedInMeeting ? `Shared ${i.sharedDate}` : "Not yet shared"}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Near Miss Learning Log</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}table{width:100%;border-collapse:collapse;font-size:13px}</style></head><body><h2>Near Miss Learning Log - Roop Cottage Residential Care Home</h2><p>Generated ${new Date().toLocaleDateString()}. A no-blame learning culture record for governance meetings and CQC.</p><table><tr><th>Date</th><th>Area</th><th>What nearly happened</th><th>Root cause</th><th>Learning</th><th>Shared</th></tr>${rows}</table></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Near-Miss Learning Log</h2>
        <p className="text-sm text-slate-600">No blame, just learning. Report what nearly went wrong, find the root cause, share the lesson - and prove a learning culture to inspectors.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{items.length}</div><div className="text-xs text-slate-500">Near misses reported</div></div>
        <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 text-center"><div className="text-2xl font-bold text-blue-700">{thisMonth}</div><div className="text-xs text-blue-700">This month</div></div>
        <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-center"><div className="text-2xl font-bold text-green-700">{shared}</div><div className="text-xs text-green-700">Lessons shared in meetings</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Report a near miss</h3>
        <div className="grid gap-3 md:grid-cols-3">
          <input type="date" value={form.date || ""} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <select value={form.area || "Medication"} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            {AREAS.map(a => <option key={a}>{a}</option>)}
          </select>
          <input value={form.reportedBy || ""} onChange={e => setForm(f => ({ ...f, reportedBy: e.target.value }))} placeholder="Reported by" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <textarea value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="What nearly happened? (no names, no blame)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm md:col-span-3" />
          <input value={form.immediateAction || ""} onChange={e => setForm(f => ({ ...f, immediateAction: e.target.value }))} placeholder="Immediate action taken" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input value={form.rootCause || ""} onChange={e => setForm(f => ({ ...f, rootCause: e.target.value }))} placeholder="Root cause (why did it nearly happen?)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input value={form.learning || ""} onChange={e => setForm(f => ({ ...f, learning: e.target.value }))} placeholder="Learning - what will we change?" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={add} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Record near miss</button>
          <button onClick={exportPrint} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Print for governance meeting</button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Trends by area (spot the repeats)</h3>
        <div className="space-y-2">
          {byArea.map(b => (
            <div key={b.area} className="flex items-center gap-2">
              <div className="w-44 text-xs text-slate-600">{b.area}</div>
              <div className="h-4 rounded bg-slate-100" style={{ width: `${(b.count / max) * 60}%`, minWidth: b.count ? "24px" : "0" }} />
              <div className="text-xs font-semibold text-slate-800">{b.count}</div>
            </div>
          ))}
        </div>
        {byArea[0] && byArea[0].count > 1 ? <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Repeat pattern detected in {byArea[0].area}. Raise a systemic fix at the next governance meeting.</div> : null}
      </div>

      <div className="space-y-3">
        {items.map(i => (
          <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-slate-900">{i.area} <span className="text-xs text-slate-500">{i.date} | reported by {i.reportedBy}</span></div>
              <div className="flex items-center gap-2">
                {i.sharedInMeeting ? <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">Lesson shared {i.sharedDate}</span> : <button onClick={() => markShared(i.id)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Mark lesson shared</button>}
                <button onClick={() => remove(i.id)} className="text-xs text-red-600">Remove</button>
              </div>
            </div>
            <div className="mt-2 grid gap-2 text-sm text-slate-700 md:grid-cols-2">
              <div><span className="font-semibold text-slate-900">Nearly happened:</span> {i.description}</div>
              <div><span className="font-semibold text-slate-900">Immediate action:</span> {i.immediateAction || "-"}</div>
              <div><span className="font-semibold text-slate-900">Root cause:</span> {i.rootCause || "-"}</div>
              <div><span className="font-semibold text-slate-900">Learning:</span> {i.learning || "-"}</div>
            </div>
          </div>
        ))}
        {items.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No near misses recorded yet. A healthy home reports many - it means staff feel safe to speak up.</div> : null}
      </div>
    </div>
  );
}